import React, { useMemo, useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import OverviewScreen from './OverviewScreen';
import WalletHubScreen from './WalletHubScreen';
import ApiKeysScreen from './ApiKeysScreen';
import IntegrationsScreen from './IntegrationsScreen';
import BrandingScreen from './BrandingScreen';
import AuditLogsScreen from './AuditLogsScreen';
import { api } from '../utils/api';
import VeritageLogo from '../components/VeritageLogo';
import './PortalScreen.css';

function HomeIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}

function WalletIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );
}

function KeyIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
    </svg>
  );
}

function PuzzleIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
    </svg>
  );
}

function PaintIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
    </svg>
  );
}

function AuditIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  );
}

function MenuIcon({ isOpen }) {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d={isOpen ? 'M5 5L15 15M15 5L5 15' : 'M3 5H17M3 10H17M3 15H17'}
        stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      />
    </svg>
  );
}

const NAV_ITEMS = [
  { key: 'overview',     label: 'Overview',     icon: HomeIcon },
  { key: 'wallet-hub',   label: 'Wallet Hub',   icon: WalletIcon },
  { key: 'api-keys',     label: 'API Keys',     icon: KeyIcon },
  { key: 'integrations', label: 'Integrations', icon: PuzzleIcon },
  { key: 'branding',     label: 'Branding',     icon: PaintIcon },
  { key: 'audit-logs',  label: 'Audit Logs',   icon: AuditIcon },
];

export default function PortalScreen({ onLogout }) {
  const location  = useLocation();
  const navigate  = useNavigate();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [firstName, setFirstName] = useState(null);

  const active = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith('/wallet-hub'))   return 'wallet-hub';
    if (p.startsWith('/api-keys'))     return 'api-keys';
    if (p.startsWith('/integrations')) return 'integrations';
    if (p.startsWith('/branding'))     return 'branding';
    if (p.startsWith('/audit-logs'))   return 'audit-logs';
    return 'overview';
  }, [location.pathname]);

  useEffect(() => {
    api.getUserProfile()
      .then(u => { setUserEmail(u.email || u.username); setFirstName(u.firstName); })
      .catch(() => {});
  }, []);

  return (
    <div className="vt-portal">
      <div className="vt-portalBackdrop" aria-hidden="true" />

      <header className="vt-topbar">
        <button className="vt-iconBtn vt-menuBtn" type="button"
          onClick={() => setIsNavOpen(v => !v)}
          aria-label={isNavOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={isNavOpen}>
          <MenuIcon isOpen={isNavOpen} />
        </button>

        <div className="vt-topbarBrand" aria-label="Veritage dashboard">
          <VeritageLogo className="vt-topbarLogo" height={26} />
          <span className="vt-topbarDivider" aria-hidden="true" />
          <span className="vt-topbarSub">Dashboard</span>
        </div>

        <nav className="vt-topnav" aria-label="Dashboard sections">
          {NAV_ITEMS.map(item => (
            <button
              key={item.key}
              type="button"
              className={['vt-topnavItem', item.key === active ? 'vt-topnavItem--active' : ''].filter(Boolean).join(' ')}
              aria-current={item.key === active ? 'page' : undefined}
              onClick={() => navigate(item.key === 'overview' ? '/' : `/${item.key}`)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="vt-topbarRight">
          <a className="vt-topbarLink" href="https://docs.veritage.io" target="_blank" rel="noreferrer">Docs</a>
          {userEmail && (
            <span className="vt-topbarUser" title={userEmail}>
              <span className="vt-topbarAvatar" aria-hidden="true">{(firstName || userEmail).trim().charAt(0).toUpperCase()}</span>
              <span className="vt-topbarEmail">{userEmail}</span>
            </span>
          )}
          <button className="vt-btn vt-btn--secondary vt-btn--sm" type="button" onClick={onLogout}>Log out</button>
        </div>
      </header>

      <div className="vt-portalBody">
        <aside className={['vt-sidenav', isNavOpen ? 'vt-sidenav--open' : ''].filter(Boolean).join(' ')}>
          <div className="vt-sidenavInner vt-surface">
            <div className="vt-sidenavBrand">
              <div className="vt-sidenavBrandContent" aria-label="Veritage dashboard">
                <VeritageLogo className="vt-sidenavLogo" height={24} />
              </div>
            </div>

            <div className="vt-sidenavLabel">Identity verification</div>
            <nav aria-label="Dashboard navigation" className="vt-sidenavNav">
              {NAV_ITEMS.map(item => {
                const isActive = item.key === active;
                const IconComponent = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={['vt-navItem', isActive ? 'vt-navItem--active' : ''].filter(Boolean).join(' ')}
                    onClick={() => { navigate(item.key === 'overview' ? '/' : `/${item.key}`); setIsNavOpen(false); }}
                  >
                    <span className="vt-navItemIcon" aria-hidden="true">
                      <IconComponent className="vt-navItemIconSvg" />
                    </span>
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>

            <div className="vt-sidenavFoot vt-muted">
              <span className="vt-sidenavPill">SOC 2</span>
              <span className="vt-sidenavPill">GDPR</span>
              <span className="vt-sidenavPill">ISO 27001</span>
            </div>
          </div>
        </aside>

        <button
          className={['vt-overlay', isNavOpen ? 'vt-overlay--visible' : ''].filter(Boolean).join(' ')}
          type="button" onClick={() => setIsNavOpen(false)}
          aria-label="Close navigation overlay" aria-hidden={!isNavOpen}
        />

        <main className="vt-main">
          <div className="vt-container">
            <Routes>
              <Route path="/"             element={<OverviewScreen userEmail={userEmail} firstName={firstName} />} />
              <Route path="/overview"     element={<OverviewScreen userEmail={userEmail} firstName={firstName} />} />
              <Route path="/wallet-hub"   element={<WalletHubScreen />} />
              <Route path="/api-keys"     element={<ApiKeysScreen />} />
              <Route path="/integrations" element={<IntegrationsScreen />} />
              <Route path="/branding"     element={<BrandingScreen />} />
              <Route path="/audit-logs"  element={<AuditLogsScreen />} />
              <Route path="*"            element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}
